import { motion } from 'framer-motion'
import { Lock, Eye, EyeOff, ArrowLeft, Check, Circle, KeyRound } from 'lucide-react'
import type { LoginStepFormProps } from '@/components/Login/loginTypes'
import { LoginAlerts } from '@/components/Login/LoginAlerts'
import { getPasswordRequirementStatus } from '@/utils/passwordRequirements'

export function LoginResetPasswordForm({ flow, theme, brandColor }: LoginStepFormProps) {
  const requirements = getPasswordRequirementStatus(flow.newPassword)
  const passwordsMatch =
    flow.confirmNewPassword.length > 0 && flow.newPassword === flow.confirmNewPassword
  const canSubmit =
    Boolean(flow.resetToken) &&
    requirements.every((item) => item.met) &&
    passwordsMatch &&
    !flow.isAnyPending
  const muted = theme.textMuted || theme.fontColorMuted || '#8a8a95'

  return (
    <motion.form
      key="resetPassword"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
      onSubmit={flow.handleResetSubmit}
      className="login-form"
      style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
    >
      <div className="login-field" style={{ textAlign: 'center' }}>
        <KeyRound size={48} style={{ color: brandColor, margin: '0 auto 16px auto', display: 'block' }} />
        <p style={{ color: muted, fontSize: '0.875rem', lineHeight: 1.6 }}>
          Crie uma senha forte para acessar o Conecta. O link do e-mail é único e só pode ser usado uma vez.
        </p>
      </div>

      <LoginAlerts flow={flow} showSuccess={false} />

      {/* Token vem só da URL do e-mail; nunca visível nem editável. */}
      <input type="hidden" name="token" value={flow.resetToken} readOnly autoComplete="off" />

      <div className="login-field">
        <label htmlFor="newPassword">Nova senha</label>
        <div className="login-input-wrapper" style={{ position: 'relative' }}>
          <Lock size={16} style={{ position: 'absolute', left: 14, color: muted, zIndex: 1 }} />
          <input
            id="newPassword"
            name="new-password"
            type={flow.showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            value={flow.newPassword}
            onChange={(e) => flow.setNewPassword(e.target.value)}
            required
            minLength={8}
            autoFocus
            spellCheck={false}
          />
          <button
            type="button"
            className="login-eye-btn"
            onClick={() => flow.setShowPassword(!flow.showPassword)}
            aria-label={flow.showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            style={{
              position: 'absolute',
              right: 12,
              background: 'none',
              border: 'none',
              color: muted,
              padding: 4,
              cursor: 'pointer',
            }}
          >
            {flow.showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div className="login-field">
        <label htmlFor="confirmNewPassword">Confirmar senha</label>
        <div className="login-input-wrapper" style={{ position: 'relative' }}>
          <Lock size={16} style={{ position: 'absolute', left: 14, color: muted, zIndex: 1 }} />
          <input
            id="confirmNewPassword"
            name="confirm-password"
            type={flow.showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Repita a senha"
            value={flow.confirmNewPassword}
            onChange={(e) => flow.setConfirmNewPassword(e.target.value)}
            required
            minLength={8}
            spellCheck={false}
          />
        </div>
        {flow.confirmNewPassword.length > 0 && !passwordsMatch && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#ef4444' }}>
            As senhas não coincidem.
          </p>
        )}

        <ul
          aria-label="Requisitos da senha"
          style={{
            listStyle: 'none',
            margin: '10px 0 0',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {requirements.map((item) => (
            <li
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: item.met ? brandColor : muted,
              }}
            >
              {item.met ? <Check size={14} aria-hidden /> : <Circle size={14} aria-hidden />}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="submit"
        className="login-submit"
        disabled={!canSubmit}
        style={{
          background: brandColor,
          boxShadow: `0 4px 15px ${brandColor}4D`,
          opacity: canSubmit ? 1 : 0.5,
        }}
      >
        {flow.isAnyPending ? <span className="login-spinner" /> : 'Definir senha'}
      </button>

      <button type="button" onClick={flow.goBack} className="login-back-btn">
        <ArrowLeft size={14} /> Voltar ao Login
      </button>
    </motion.form>
  )
}
